import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Form } from "react-bootstrap";
import {
  debounce,
  find,
  get,
  isEmpty,
  isNil,
  keyBy,
  map,
  some,
  transform,
} from "lodash";
import { FaUndo } from "react-icons/fa";
import { push } from "store/actions/router";
import { getPaginatedEmployees } from "actions/employerActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { formatCurrency } from "utils/number";
import { FiSearch } from "react-icons/fi";
import type { RootState } from "types/state";

import IconMessageCard from "components/IconMessageCard";
import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import StatusBox from "components/StatusBox";
import Paginator from "components/Paginator";
import IconTable from "components/IconTable";

// Faithful subset of the getPaginatedEmployees row shape that this component
// actually reads (state.employer.paginatedEmployees is `unknown[]` in RootState).
interface ContributionChangeRequest {
  status: string | null;
  userNewContributionAmount?: number | null;
}

interface Contribution {
  amount: number;
  contributionChangeRequests?: ContributionChangeRequest[] | null;
}

interface Employee {
  id: string;
  employeeId: string;
  contribution?: Contribution | null;
  userProfile: {
    firstName: string | null;
    lastName: string | null;
    lastFourOfSsn: string | null;
  };
}

interface Group {
  id: string;
  name?: string;
}

// Row shape produced by _getReviewExisitingData and consumed by IconTable +
// the customComponent callbacks (which read `emp`).
interface ReviewExistingRow {
  employeeName: string;
  lastFourOfSsn: string | null;
  contributionAmount: string;
  status: React.ReactNode;
  undoButton: React.ReactNode;
  emp: Employee;
}

const isLoadingSelector = createLoadingSelector(
  employerConstants.GET_PAGINATED_EMPLOYEES
);
const isErrorSelector = createErrorSelector(
  employerConstants.GET_PAGINATED_EMPLOYEES
);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isLoadingSelector(state),
    // paginatedEmployees is `unknown[]` in RootState — cast at the boundary.
    employees: state.employer.paginatedEmployees as Employee[],
    employeeCount: state.employer.employeeCount,
    error: isErrorSelector(state),
  };
};

const mapDispatchToProps = {
  getPaginatedEmployees,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Injected by react-step-wizard at runtime — the parent <StepWizard> provides
  // these, they are not passed explicitly. Optional + non-null-asserted at call.
  goToNamedStep?: (stepName: string) => void;
  stepName?: string;
  // Passed by parent PayrollWorkflow.
  setSkipped: (skipped: Record<string, Employee>) => void;
  setRemoved: (removed: Record<string, Employee>) => void;
  skippedContributions: Record<string, Employee>;
  removedEmployees: Record<string, Employee>;
  group: Group;
  // PropTypes marked `payroll` .isRequired, but the parent never passes it and
  // it is never read as a prop — optional here to match real usage.
  payroll?: object;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  limit: number;
  offset: number;
  search: string;
  checkedRows: Record<string, Employee>;
  initialFetching: boolean;
  page: number;
  // Initialized in the constructor but never read — preserved for fidelity.
  group: object;
  payroll: object;
}

export class ReviewExisting extends React.PureComponent<Props, State> {
  _textInput: HTMLInputElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      limit: 25,
      offset: 0,
      search: "",
      checkedRows: {},
      initialFetching: true,
      page: 0,
      group: {},
      payroll: {},
    };
  }

  componentDidMount() {
    void this._getPaginatedEmployees().then(() => {
      this.setState({ initialFetching: false });
    });
  }

  // _getPaginatedEmployees = () => {
  //   const groupId = this.props.group?.id;
  //   if (!groupId) {
  //     console.error("Group ID is missing");
  //     return Promise.resolve([]); // Ensure it returns a resolved Promise with an empty array
  //   }
  //   return this.props.getPaginatedEmployees(this.props.client, {
  //     groupId,
  //     limit: this.state.limit,
  //     offset: this.state.offset,
  //     search: this.state.search,
  //   });
  // };

  _getPaginatedEmployees = () => {
    return this.props.getPaginatedEmployees(
      this.props.client as ApolloClient<object>,
      {
        groupId: this.props.group.id,
        limit: this.state.limit,
        offset: this.state.offset,
        search: this.state.search,
      }
    );
  };

  _removeFromSkipped = (employeeId: string) => {
    const newSkipped = { ...this.props.skippedContributions };
    delete newSkipped[employeeId];
    this.props.setSkipped(newSkipped);
  };
  _removeFromRemoved = (employeeId: string) => {
    const newRemoved = { ...this.props.removedEmployees };
    delete newRemoved[employeeId];
    this.props.setRemoved(newRemoved);
  };

  _getEmployeeStatus = (
    employeeId: string,
    contribution: Contribution | null = {} as Contribution
  ) => {
    const statusAndUndo: {
      status: React.ReactNode;
      undoButton: React.ReactNode;
    } = {
      status: null,
      undoButton: null,
    };

    const changeRequests = get(contribution, "contributionChangeRequests", []);
    const hasZeroContribution = !contribution || contribution.amount === 0;

    const hasPendingChange = some(
      changeRequests,
      (c) => c.status === "CHANGED"
    );

    if (this.props.skippedContributions[employeeId]) {
      statusAndUndo.status = <StatusBox status="Skipped" type="dark" />;
      statusAndUndo.undoButton = (
        <FaUndo size={16} onClick={() => this._removeFromSkipped(employeeId)} />
      );
    } else if (this.props.removedEmployees[employeeId]) {
      statusAndUndo.status = <StatusBox status="Removed" type="danger" />;
      statusAndUndo.undoButton = (
        <FaUndo size={16} onClick={() => this._removeFromRemoved(employeeId)} />
      );
    } else if (hasPendingChange) {
      statusAndUndo.status = (
        <StatusBox status="Has Pending Change" type="pending" />
      );
    } else if (hasZeroContribution) {
      statusAndUndo.status = (
        <StatusBox status="Paused Contributions" type="light" />
      );
    } else {
      statusAndUndo.status = <StatusBox status="Active" type="success" />;
    }

    return statusAndUndo;
  };

  _setChecked = (employee: Employee, isChecked: boolean) => {
    let newChecked = { ...this.state.checkedRows };
    if (!isChecked) {
      delete newChecked[employee.employeeId];
      this.setState({ checkedRows: newChecked });
      return;
    }
    this.setState({
      checkedRows: {
        ...this.state.checkedRows,
        [employee.employeeId]: employee,
      },
    });
  };

  _getReviewExisitingData = (): ReviewExistingRow[] => {
    return map(this.props.employees, (emp) => {
      const {
        userProfile: { firstName, lastName, lastFourOfSsn },
      } = emp;
      const { status, undoButton } = this._getEmployeeStatus(
        emp.employeeId,
        emp.contribution
      );
      const lastApprovedAmount = find(
        get(emp, "contribution.contributionChangeRequests", []),
        (c) => c.status === "APPROVED"
      );

      let contributionAmount;

      if (!emp.contribution) {
        contributionAmount = formatCurrency(0);
      } else {
        contributionAmount = !isNil(lastApprovedAmount)
          ? // Quirk preserved: only the object is null-checked, not the field —
            // a null/undefined userNewContributionAmount is passed through and
            // coerced by formatCurrency, exactly as the original JS did.
            formatCurrency(
              lastApprovedAmount.userNewContributionAmount as number
            )
          : formatCurrency(emp.contribution.amount);
      }

      const fullName = `${firstName} ${lastName}`;

      return {
        employeeName: fullName,
        lastFourOfSsn,
        contributionAmount,
        status,
        undoButton,
        emp,
      };
    });
  };

  _buildReviewExistingColumns = () => {
    const columns = [
      {
        label: "",
        customComponent: (props: ReviewExistingRow) => {
          const { undoButton } = this._getEmployeeStatus(props.emp.employeeId);
          return (
            <div style={{ width: 25, textAlign: "center", paddingLeft: "8px" }}>
              {!undoButton && (
                <Form.Check
                  type="checkbox"
                  name="selectAllPending"
                  checked={!!this.state.checkedRows[props.emp.employeeId]}
                  onChange={(e) =>
                    this._setChecked(props.emp, e.target.checked)
                  }
                />
              )}
            </div>
          );
        },
      },
      {
        label: "Employee Name",
        key: "employeeName",
      },

      {
        label: "Last 4 SSN",
        key: "lastFourOfSsn",
      },

      {
        label: "Current Amount",
        key: "contributionAmount",
      },
      {
        label: "Status",
        key: "status",
      },
      {
        customComponent: (props: ReviewExistingRow) => {
          const { undoButton } = this._getEmployeeStatus(props.emp.employeeId);
          return <div className="undo-button">{undoButton}</div>;
        },
      },
    ];
    return columns;
  };

  _addToSkipped = () => {
    const newSkipped = {
      ...this.props.skippedContributions,
      ...this.state.checkedRows,
    };
    this.props.setSkipped(newSkipped);
    this.setState({ checkedRows: {} });
  };
  _addToRemoved = () => {
    const newRemoved = {
      ...this.props.removedEmployees,
      ...this.state.checkedRows,
    };
    this.props.setRemoved(newRemoved);
    this.setState({ checkedRows: {} });
  };

  _onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        search: e.target.value,
        offset: 0,
      },
      () =>
        void this._getPaginatedEmployees().then(() => {
          const empsById = keyBy(this.props.employees, "id");
          const newChecked = transform(
            this.state.checkedRows,
            (acc: Record<string, boolean>, val, key) => {
              if (empsById[key]) {
                acc[key] = true;
              }
              return acc;
            },
            {}
          );
          // Quirk preserved: this rebuild stores `true` booleans into
          // checkedRows, whereas every other path stores Employee objects.
          // Cast keeps the original mixed-shape runtime behavior intact.
          this.setState({
            checkedRows: newChecked as unknown as Record<string, Employee>,
            page: 0,
          });
        })
    );
  };

  _debounceOnChange = debounce(this._onSearchChange, 500);

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedEmployees()
    );
  };

  _clearSearch = () => {
    this._textInput!.value = "";
    this.setState(
      {
        search: "",
        offset: 0,
      },
      () => this._getPaginatedEmployees()
    );
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }
    const showBlankSlate = isEmpty(this.props.employees);

    const columns = this._buildReviewExistingColumns();
    const data = this._getReviewExisitingData();

    return (
      <div>
        <Card>
          <div className="workflow-widget">
            <div className="action-header">
              <p className="group-info">
                <span className="group-name">
                  {this.props.group.name}:{" "}
                  <span
                    className="invite-number"
                    data-testid="contributions-being-processed"
                  >
                    Contributions being processed (
                    <span className="number">{this.props.employeeCount}</span>)
                  </span>
                </span>
              </p>

              <div className="btn-row">
                <Button
                  onClick={this._addToRemoved}
                  name="action"
                  color="red"
                  btnLabel="Remove from Plan"
                  size="sm"
                  disabled={isEmpty(this.state.checkedRows)}
                />
                <Button
                  onClick={this._addToSkipped}
                  color="secondary"
                  name="action"
                  size="sm"
                  btnLabel="Skip Deduction"
                  disabled={isEmpty(this.state.checkedRows)}
                />
              </div>
            </div>
            <div style={{ paddingBottom: 5 }}>
              <Col as={Col} md={3}>
                <Form.Control
                  name="search"
                  placeholder="Search Employee"
                  data-testid="search-employee"
                  onChange={this._debounceOnChange}
                  ref={(node: HTMLInputElement | null) => {
                    this._textInput = node;
                  }}
                />
              </Col>
            </div>
          </div>

          {this.props.isFetching && <IconSpinner centered />}
          {showBlankSlate && (
            <IconMessageCard
              header={"There were no employees found matching your search."}
              subheader={"Would you like to clear your search?"}
              icon={<FiSearch color="white" stroke="#60A4BF" size={16} />}
              actionText={"Clear Search"}
              onClick={this._clearSearch}
            />
          )}
          {!showBlankSlate && (
            <>
              <IconTable columns={columns} data={data} />
              <div className="pagination-row">
                <Paginator
                  onChange={this._onPageChange}
                  pageCount={this.props.employeeCount / this.state.limit}
                  page={this.state.page}
                />
              </div>
            </>
          )}
        </Card>
        <div className="process-payroll-button-row">
          <Button
            onClick={() => this.props.push("/dashboard/contributions")}
            btnLabel="Cancel"
            name="cancel"
            color="cancel"
          />
          <Button
            name="action"
            onClick={() => this.props.goToNamedStep!("approvals")}
          />
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(ReviewExisting));
